import { useMemo } from "react";
import { useMyFeed } from "../hooks/useTasks";
import { useUiStore } from "../state/uiStore";
import { isOpenStatus } from "../lib/taskVM";
import { isoToDayKey, todayKey, formatDay, isOverdue } from "../lib/date";

interface Notif {
  id: string;
  taskId: string;
  kind: "overdue" | "due_today" | "due_soon";
  title: string;
  detail: string;
}

/** Notifications v1 — dérivées localement du feed (échéances proches / retards).
 * ponytail: le Notification Engine IC (DT F-06) n'expose pas encore d'API de
 * lecture ; brancher son inbox quand elle existera. */
export function NotificationsScreen() {
  const feed = useMyFeed();
  const selectTask = useUiStore((s) => s.selectTask);
  const selectedTaskId = useUiStore((s) => s.selectedTaskId);
  const notifPrefs = useUiStore((s) => s.prefs.notifs);

  const notifs = useMemo(() => {
    const today = todayKey();
    const soonLimit = new Date();
    soonLimit.setDate(soonLimit.getDate() + 3);
    const out: Notif[] = [];
    for (const f of feed.data ?? []) {
      if (!f.due_date || !isOpenStatus(f.status)) continue;
      const dueKey = isoToDayKey(f.due_date);
      if (isOverdue(dueKey)) {
        if (!notifPrefs.overdue) continue;
        out.push({
          id: `${f.id}-overdue`,
          taskId: f.id,
          kind: "overdue",
          title: f.title,
          detail: `En retard depuis le ${formatDay(dueKey)}`,
        });
      } else if (dueKey === today) {
        if (!notifPrefs.due_today) continue;
        out.push({
          id: `${f.id}-today`,
          taskId: f.id,
          kind: "due_today",
          title: f.title,
          detail: "Échéance aujourd'hui",
        });
      } else if (dueKey <= isoToDayKey(soonLimit.toISOString())) {
        if (!notifPrefs.due_soon) continue;
        out.push({
          id: `${f.id}-soon`,
          taskId: f.id,
          kind: "due_soon",
          title: f.title,
          detail: `Échéance le ${formatDay(dueKey)}`,
        });
      }
    }
    const rank = { overdue: 0, due_today: 1, due_soon: 2 };
    return out.sort((a, b) => rank[a.kind] - rank[b.kind]);
  }, [feed.data, notifPrefs]);

  const KIND_STYLE = {
    overdue: {
      icon: "⛒",
      color: "var(--danger)",
      bg: "var(--danger-soft)",
      event: "tasks.task.overdue",
    },
    due_today: {
      icon: "◷",
      color: "var(--accent)",
      bg: "var(--accent-soft)",
      event: "tasks.task.due_today",
    },
    due_soon: {
      icon: "◔",
      color: "var(--ink-muted)",
      bg: "var(--bg-chip)",
      event: "tasks.task.due_soon",
    },
  } as const;

  return (
    <>
      <div className="view-header">
        <div className="view-title">Notifications</div>
        <div className="view-caption">Échéances et retards de vos tâches</div>
      </div>
      <div className="main-scroll">
        {feed.isLoading && <div className="empty-state">Chargement…</div>}
        <div style={{ marginTop: 14, maxWidth: 720 }}>
          {notifs.map((n) => {
            const s = KIND_STYLE[n.kind];
            return (
              <button
                key={n.id}
                className={`task-row${n.taskId === selectedTaskId ? " selected" : ""}`}
                style={{ width: "100%" }}
                onClick={() =>
                  selectTask(n.taskId === selectedTaskId ? null : n.taskId)
                }
              >
                <span
                  className="avatar"
                  style={{
                    width: 24,
                    height: 24,
                    fontSize: 12,
                    background: s.bg,
                    color: s.color,
                  }}
                >
                  {s.icon}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span
                    className="task-title"
                    style={{ display: "block", fontWeight: 600 }}
                  >
                    {n.title}
                  </span>
                  <span style={{ fontSize: 11, color: s.color }}>
                    {n.detail}
                  </span>
                </span>
                <span
                  className="mono"
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    color: "var(--ink-ghost)",
                  }}
                >
                  {s.event}
                </span>
              </button>
            );
          })}
          {notifs.length === 0 && !feed.isLoading && (
            <div className="empty-state">
              Rien à signaler — aucune échéance proche.
            </div>
          )}
        </div>
      </div>
    </>
  );
}
